import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { useEstablishmentInfrastructures } from "@/common/hooks/queries/useEstablishmentInfrastructures";
import { useEstablishmentTechnicalProfessionals } from "@/common/hooks/queries/useEstablishmentTechnicalProfessionals";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { useUpdateEstablishmentCompanyMaintenanceModels } from "@/features/establishments/hooks/maintenanceModels/useUpdateEstablishmentCompanyMaintenanceModels";
import { updateEstablishmentCompanyMaintenanceModelsSchema, UpdateEstablishmentCompanyMaintenanceModelsValidationSchema } from "@/features/establishments/schemas/updateEstablishmentCompanyMaintenanceModels.schema";
import { updateEstablishmentCompanyMaintenanceModelsDefaultValues } from "@/features/establishments/utils/updateEstablishmentCompanyMaintenanceModelsDefaultValues";
import { EstablishmentInfrastructuresTransformer } from "@/features/maintenance/establishmentInfrastructures/services/establishmentInfrastructuresTransformer";
import { EstablishmentTechnicalProfessionalsTransformer } from "@/features/maintenance/establishmentTechnicalProfessionals/services/establishmentTechnicalProfessionalsTransformer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface UpdateMaintenanceGeneralInformationProps {
  establishmentCompany: EstablishmentCompanyData;
  disabled?: boolean;
}

const UpdateMaintenanceGeneralInformation = ({ establishmentCompany, disabled }: UpdateMaintenanceGeneralInformationProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchEstablishmentInfraestructures, setSearchEstablishmentInfraestructures] = useDebounce('');
  const [searchEstablishmentTechnicalProfessionals, setSearchEstablishmentTechnicalProfessionals] = useDebounce('');

  const {
    data: establishmentInfrastructuresOptions,
  } = useEstablishmentInfrastructures({
    filters: {
      search: searchEstablishmentInfraestructures,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentInfrastructuresTransformer.optionsTransformer(),
    }),
  });

  const {
    data: establishmentTechnicalProfessionals,
  } = useEstablishmentTechnicalProfessionals({
    filters: {
      search: searchEstablishmentTechnicalProfessionals,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentTechnicalProfessionalsTransformer.optionsTransformer(),
    }),
  });

  const {
    mutate: updateEstablishmentCompany,
    isPending: updateEstablishmentCompanyIsPending,
  } = useUpdateEstablishmentCompanyMaintenanceModels({
    establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false);
    },
    onError: (error) => {
      setIsOpen(false);
      reset(updateEstablishmentCompanyMaintenanceModelsDefaultValues(establishmentCompany))
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateEstablishmentCompanyMaintenanceModelsValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanyMaintenanceModelsSchema),
    defaultValues: updateEstablishmentCompanyMaintenanceModelsDefaultValues(establishmentCompany),
  });

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyMaintenanceModelsValidationSchema> = (data) => updateEstablishmentCompany(data);

  useEffect(() => {
    reset(updateEstablishmentCompanyMaintenanceModelsDefaultValues(establishmentCompany));
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={disabled}
        Trigger={
          <Button
            operationKlassToCheckPermission="Admin::Establishment::Companies::UpdateMaintenanceModels"
            disabled={disabled}
          >
            Editar
          </Button>
        }
        useCancellableFooter
        showClose={false}
        title="Editar informações gerais de manutenção"
        className="min-w-[720px]"
        bodyClassName="text-white-800"
        titleClass="text-2xl"
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        disabled={updateEstablishmentCompanyIsPending}
        confirmButtonIsLoading={updateEstablishmentCompanyIsPending}
        onCancel={reset}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form>
            <div className="flex flex-col gap-4">
              <SelectController
                label="Infraestrutura para manutenção"
                name="maintenance_infrastructures"
                control={control}
                options={establishmentInfrastructuresOptions}
                onInputChange={setSearchEstablishmentInfraestructures}
                isMulti
                markAsRequired
                isDisabled={updateEstablishmentCompanyIsPending}
                errorMessage={errors?.maintenance_infrastructures?.message as string}
                operationKlassToCheckPermission="Admin::Maintenance::EstablishmentInfrastructures::Filter"
                optionLabelFormat="icon"
              />

              <SelectController
                label="Equipe técnica"
                name="maintenance_technical_professionals"
                control={control}
                options={establishmentTechnicalProfessionals}
                onInputChange={setSearchEstablishmentTechnicalProfessionals}
                isMulti
                markAsRequired
                isDisabled={updateEstablishmentCompanyIsPending}
                errorMessage={errors?.maintenance_technical_professionals?.message as string}
                operationKlassToCheckPermission="Admin::Maintenance::EstablishmentTechnicalProfessionals::Filter"
                optionLabelFormat="icon"
              />
            </div>
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
};

export default UpdateMaintenanceGeneralInformation;
